import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SimpleOauth2User } from 'src/app/cc-api/models/simple-oauth-2user';
import { ActivatedRoute, Router } from '@angular/router';
import { UiStackControllerService } from 'src/app/cc-api/services';
import { ContextUpdatesService } from 'src/app/services/context-updates.service';
import { Subscription } from 'rxjs';
import {
  deploymentValueNameMapping,
  STATUS_MAPPING,
} from 'src/app/constants/deployments';
import dayjs from 'dayjs';
import { NbPopoverDirective } from '@nebular/theme';
import { triggerAnalyticsEvent } from 'src/app/utils/analytics/trigger-analytics';

@Component({
  selector: 'app-release-notification-panel',
  templateUrl: './release-notification-panel.component.html',
  styleUrls: ['./release-notification-panel.component.scss'],
})
export class ReleaseNotificationPanelComponent implements OnInit, OnDestroy {
  @ViewChild(NbPopoverDirective) popover: NbPopoverDirective;

  user: SimpleOauth2User;
  isHoveredNotification = false;
  stackName: string = '';
  notifications: any[] = [];
  showEmpty: boolean = false;
  loadingNotification: boolean = false;
  private clusterCall: any;
  private contextSubscription: Subscription;

  constructor(
    private uiStackControllerService: UiStackControllerService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private contextUpdatesService: ContextUpdatesService,
  ) {}

  ngOnInit(): void {
    this.contextSubscription =
      this.contextUpdatesService.successfulContextUpdates.subscribe(
        (context) => {
          this.stackName = context.currentContext.blueprint;
          if (this.stackName) {
            this.callStackClusterApis();
            this.resetClusterCallInterval();
          } else {
            this.clearClusterCallInterval();
          }
        },
      );
    this.clusterCall = setInterval(() => {
      if (this.stackName) {
        this.callStackClusterApis();
      }
    }, 300000);
  }

  ngOnDestroy() {
    this.clearClusterCallInterval();
    if (this.contextSubscription) {
      this.contextSubscription.unsubscribe();
    }
  }

  get notificationCount(): number {
    return this.notifications.length;
  }

  callStackClusterApis() {
    this.loadingNotification = true;
    this.uiStackControllerService
      .getClustersOverviewUsingGET(this.stackName)
      .subscribe(
        (res) => {
          this.loadingNotification = false;
          this.setTableData(res);
        },
        (err) => {
          this.loadingNotification = false;
        },
      );
  }

  setTableData(environmentList) {
    const oneWeekAgo = dayjs().subtract(7, 'day');
    this.notifications = environmentList
      .filter((info) => {
        const lastRelease = info?.lastRelease;
        if (!lastRelease) return false;
        const statusText = STATUS_MAPPING[lastRelease.status]?.statusText;
        if (statusText !== 'FAIL') return false;

        const finishedOn = dayjs(
          lastRelease.finishedOn || lastRelease.createdOn,
        );
        return finishedOn.isAfter(oneWeekAgo);
      })
      .map((info) => {
        const lastRelease = info?.lastRelease;
        const status = STATUS_MAPPING[lastRelease?.status] || {};
        return {
          notificationMessage: `${
            deploymentValueNameMapping[lastRelease?.releaseType] || ''
          } ${status?.statusText || ''}`,
          notificationColor: status?.background,
          environmentName: info?.cluster?.name || '--',
          clusterState: info?.clusterState || '--',
          clusterId: info?.cluster?.id,
          triggeredDate: lastRelease?.finishedOn || lastRelease?.createdOn,
          lastReleaseId: lastRelease?.id || '',
          userTriggered: lastRelease?.triggeredBy || '',
        };
      })
      .sort(
        (a, b) =>
          new Date(b.triggeredDate).getTime() -
          new Date(a.triggeredDate).getTime(),
      );
    this.showEmpty = this.notifications.length === 0;
  }

  navigateToEnvironment(notification: any) {
    const url =
      '/stack/' +
      this.stackName +
      '/releases/cluster/' +
      notification.clusterId +
      '/dialog/release-details/' +
      notification.lastReleaseId;
    this.router.navigate([url]);
    this.popover?.hide();
  }

  private resetClusterCallInterval() {
    this.clearClusterCallInterval();
    this.clusterCall = setInterval(() => {
      if (this.stackName) {
        this.callStackClusterApis();
      }
    }, 300000);
  }

  private clearClusterCallInterval() {
    if (this.clusterCall) {
      clearInterval(this.clusterCall);
      this.clusterCall = null;
    }
  }

  onClickHandleEvent() {
    triggerAnalyticsEvent(`NOTIFICATION_PANEL_OPENED`);
    this.callStackClusterApis();
  }
}
